import { FC, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { NAV_ITEMS } from '@/utils/constants';
import { cn } from '@/utils/cn';
import { useAuth } from '@/contexts/AuthContext';
import logo from '@/assets/logo.png';

export const Header: FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, isLoggedIn, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // 路由变化时关闭菜单
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  const isActive = (href: string) => {
    if (href === '/' && location.pathname === '/') return true;
    if (href !== '/' && location.pathname.startsWith(href)) return true;
    return false;
  };

  return (
    <header
      className={cn(
        'sticky top-0 z-50 glass border-b transition-all duration-300',
        scrolled
          ? 'border-apple-separator'
          : 'border-transparent'
      )}
    >
      <Container>
        <nav className="flex items-center justify-between h-12">

          {/* Logo */}
          <Link
            to="/"
            className="flex items-center space-x-2 flex-shrink-0"
            style={{ letterSpacing: '-0.015em' }}
          >
            <img src={logo} alt="白茆AI" className="w-7 h-7 rounded-[7px]" />
            <span className="text-[17px] font-semibold text-apple-dark">白茆AI</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'px-3 py-1.5 rounded-full text-sm transition-colors duration-150',
                  isActive(item.href)
                    ? 'text-apple-dark font-medium'
                    : 'text-apple-gray hover:text-apple-dark'
                )}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* User Actions (Desktop) */}
          <div className="hidden md:flex items-center space-x-2">
            {isLoggedIn ? (
              <>
                <Link
                  to="/dashboard"
                  className="text-sm text-apple-gray hover:text-apple-dark transition-colors px-3 py-1.5"
                >
                  控制台
                </Link>
                <span className="text-sm text-apple-gray">
                  {user?.email?.split('@')[0]}
                </span>
                <Button onClick={logout} variant="outline" size="sm">
                  退出
                </Button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-sm text-apple-gray hover:text-apple-dark transition-colors px-3 py-1.5"
                >
                  登录
                </Link>
                <Button as={Link} to="/register" size="sm">
                  注册
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-apple-gray hover:text-apple-dark rounded-full hover:bg-apple-light-gray transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="菜单"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </nav>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-apple-separator animate-fade-in">
            <div className="flex flex-col space-y-1">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'px-3 py-2.5 rounded-apple text-[15px] transition-colors',
                    isActive(item.href)
                      ? 'text-apple-dark font-medium bg-apple-light-gray'
                      : 'text-apple-gray hover:text-apple-dark hover:bg-apple-light-gray'
                  )}
                >
                  {item.label}
                </Link>
              ))}

              <div className="pt-3 mt-2 border-t border-apple-separator space-y-2">
                {isLoggedIn ? (
                  <>
                    <Link
                      to="/dashboard"
                      className="block px-3 py-2.5 text-[15px] text-apple-gray hover:text-apple-dark hover:bg-apple-light-gray rounded-apple transition-colors"
                    >
                      控制台
                    </Link>
                    <div className="px-3 py-1 text-sm text-apple-gray">{user?.email}</div>
                    <Button
                      onClick={logout}
                      variant="outline"
                      className="w-full justify-center"
                    >
                      退出登录
                    </Button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="block px-3 py-2.5 text-[15px] text-apple-gray hover:text-apple-dark hover:bg-apple-light-gray rounded-apple transition-colors"
                    >
                      登录
                    </Link>
                    <Button as={Link} to="/register" className="w-full justify-center">
                      注册
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </Container>
    </header>
  );
};
